import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import find from 'lodash/find';
import get from 'lodash/get';
import React, { useCallback, useContext } from 'react';
import set from 'lodash/set';
import styled from 'styled-components';

import { LinkToolbarButton, PlateProvider } from '@udecode/plate';
import FormContext from 'src/presentation/contexts/FormContext';
import { FIELDS_MAPPER } from '../chatConstants';
import { TextEditor } from 'src/presentation/components/agent_desktop_components';
import useEditorRef, { linkPlugin } from './useEditorRef';
import { useDebounced } from '@ad-scripts/presentation/components/hooks/debounce_hooks';
import { serializeHtml } from '@ad-scripts/presentation/components/text_editor_new/lib/serialize_html';
import useInitialValue from '@ad-scripts/presentation/components/text_editor_new/hooks/use_initial_value';

// TODO: Remove this file as part of the clean up of sc[178007]. This component is now covered by the new HtmlEditor component.
const PrivacyPolicyEditor = () => {
  const editorRef = useEditorRef(FIELDS_MAPPER.PRIVACY_DISCLAIMER_BODY.path, [linkPlugin]);
  const { errors, workingState, updateState } = useContext(FormContext);
  const err = find(errors, error => error.attr === FIELDS_MAPPER.PRIVACY_DISCLAIMER_BODY.path);
  const privacyDisclaimerBody = get(workingState, FIELDS_MAPPER.PRIVACY_DISCLAIMER_BODY.path, false);
  const initialValue = useInitialValue(privacyDisclaimerBody || '', editorRef.current);

  const onPrivacyDisclaimerChange = useCallback(() => {
    const changes = set({}, FIELDS_MAPPER.PRIVACY_DISCLAIMER_BODY.path, serializeHtml(editorRef.current));
    updateState(changes, { replaceArrayValues: true });
  }, [editorRef, updateState]);
  const debouncedOnChange = useDebounced(onPrivacyDisclaimerChange, 500, {});

  return (
    <StyledPolicyEditor>
      <PlateProvider
        id={FIELDS_MAPPER.PRIVACY_DISCLAIMER_BODY.path}
        editor={editorRef.current}
        initialValue={initialValue}
        onChange={debouncedOnChange}
        plugins={editorRef.current.plugins}
      >
        <StyledToolbar>
          <LinkToolbarButton id={FIELDS_MAPPER.PRIVACY_DISCLAIMER_BODY.path} icon={<FontAwesomeIcon icon={faLink} />} />
        </StyledToolbar>

        <StyledTextEditorWrapper>
          <TextEditor
            editorId={FIELDS_MAPPER.PRIVACY_DISCLAIMER_BODY.path}
            data-aid={FIELDS_MAPPER.PRIVACY_DISCLAIMER_BODY.path}
            editorRef={editorRef}
            placeholder={'By using chat, you agree to our company’s privacy policy.'}
          />
        </StyledTextEditorWrapper>
        {err && <ErrorMessage>{err.detail}</ErrorMessage>}
      </PlateProvider>
    </StyledPolicyEditor>
  );
};

const StyledToolbar = styled.div`
  border-bottom: 1px solid ${p => p.theme.colors.gray400};
  display: flex;
  gap: 8px;
  align-items: center;
  height: 35px;
`;

const StyledTextEditorWrapper = styled.div`
  height: 108px;
  overflow-y: auto;
`;

const StyledPolicyEditor = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => (p.hasError ? p.theme.colors.red400 : p.theme.colors.gray400)};
  border-radius: 4px;
  margin-top: 8px;
  margin-bottom: 8px;
  position: relative;

  & [data-slate-editor='true'] {
    padding: 8px;
  }

  &:focus-within {
    border-color: ${p => p.theme.colors.green400};
  }

  & .slate-ToolbarButton-active,
  & .slate-ToolbarButton:hover {
    color: ${p => p.theme.colors.green400};
  }
`;

const ErrorMessage = styled.p`
  margin-left: 4px;
  margin-top: 0;
  color: ${p => p.theme.colors.red400};
`;

export default PrivacyPolicyEditor;
